/**
 * PDF estilo HangSix (HTML → canvas → jsPDF).
 * Usado por Calculadora Solar e Consulta de Propostas.
 * Empresa: f0c6de5a-eaf9-4530-a6a7-28140091897d
 */

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { supabase } from '../../supabaseClient'
import { buildHangSixPropostaHtml, escapeHtmlHangSix } from '../hangSixPropostaHtml'
import instalacao01 from '../../img_instalacao/Instalacao01.jpeg'
import instalacao02 from '../../img_instalacao/Instalacao02.jpeg'
import instalacao03 from '../../img_instalacao/Instalacao03.jpeg'
import instalacao04 from '../../img_instalacao/Instalacao04.jpeg'
import autoconsumoImg from '../../img_instalacao/Autoconsumo.png'
import {
  formatDateBR,
  getBase64FromUrl,
  slugNomeArquivo
} from './constantes'
import { htmlComImagensEmbutidas, nomeArquivoHtmlDePdf, salvarHtmlProposta } from './salvarHtml'

const fmtKwp = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtKw = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtMoeda = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n) || 0)

async function carregarEmpresa(userEmpresa) {
  const id = userEmpresa?.id
  if (id) {
    const { data, error } = await supabase.from('empresa').select('*').eq('id', id).single()
    if (!error && data) return data
  }
  return userEmpresa || {}
}

function montarEnderecoEmpresa(empresa) {
  const partes = [
    empresa.endereco,
    [empresa.cidade, empresa.estado].filter(Boolean).join(' - '),
    empresa.cep
  ].filter((p) => String(p || '').trim())
  return partes.join(' | ')
}

/**
 * @param {{ dados: object, userEmpresa: object }} args
 * @returns {Promise<{ fileName: string, htmlFileName: string, tipo: 'pdf' }>}
 */
export async function gerarPdfHangSix({ dados, userEmpresa }) {
  const empresa = await carregarEmpresa(userEmpresa)
  const logoUrl = empresa.logo || empresa.logo_url || userEmpresa?.logo_url || ''
  const logoSrc = logoUrl ? (await getBase64FromUrl(logoUrl)) || logoUrl : ''

  const nomeEmpresa = (empresa.empresa || empresa.nome || userEmpresa?.nome || '').trim()
  const razaoSocial = (empresa.razao_social || '').trim()
  const contato = (empresa.contato || '').trim()
  const email = (empresa.email || '').trim()
  const endereco = montarEnderecoEmpresa(empresa)

  const totalComMajoracao = Number(dados.totalProposta) || 0
  const economiaAnual = Math.round((Number(dados.totalComPorcentagem) || 0) * 0.15)
  const paybackAnos = economiaAnual > 0 ? totalComMajoracao / economiaAnual : 0
  const qtd = dados.quantidadePaineis || 0
  const potModulo = dados.moduloPotencia || 0
  const moduloNome = dados.moduloNome || 'Módulo'

  const cpfLinha = dados.cpf
    ? `<li><strong>CPF:</strong> <span>${escapeHtmlHangSix(dados.cpf)}</span></li>`
    : ''

  const htmlDoc = buildHangSixPropostaHtml({
    logoSrc,
    nomeCliente: escapeHtmlHangSix((dados.nomeCliente || '').trim()),
    dataProposta: escapeHtmlHangSix(formatDateBR(dados.dataProposta)),
    dataValidade: escapeHtmlHangSix(formatDateBR(dados.dataValidade)),
    cpfLinha,
    consumoStr: escapeHtmlHangSix(`${dados.kwh || 0} kWh`),
    kwpStr: escapeHtmlHangSix(`${fmtKwp(dados.projetoKwp)} kWp`),
    inversorStr: escapeHtmlHangSix(`${fmtKw(dados.inversor)} kW`),
    modulosStr: escapeHtmlHangSix(`${qtd}x ${moduloNome} ${potModulo}W`),
    investimentoStr: escapeHtmlHangSix(fmtMoeda(totalComMajoracao)),
    investimentoValor: totalComMajoracao,
    economiaAnualStr: escapeHtmlHangSix(fmtMoeda(economiaAnual)),
    economiaAnualValor: economiaAnual,
    paybackAnosStr: escapeHtmlHangSix(
      paybackAnos > 0
        ? `${paybackAnos.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} anos`
        : '—'
    ),
    empresaNome: escapeHtmlHangSix(nomeEmpresa.toUpperCase()),
    empresaSlogan: escapeHtmlHangSix(razaoSocial),
    contatoStr: escapeHtmlHangSix([contato, email].filter(Boolean).join(' | ')),
    enderecoStr: escapeHtmlHangSix(endereco),
    gallery: [instalacao03, instalacao02, instalacao04, instalacao01],
    autoconsumoSrc: autoconsumoImg
  })

  const iframe = document.createElement('iframe')
  iframe.setAttribute('title', 'proposta-hangsix')
  iframe.style.position = 'fixed'
  iframe.style.left = '-9999px'
  iframe.style.top = '0'
  iframe.style.width = '210mm'
  iframe.style.minHeight = '297mm'
  iframe.style.border = 'none'
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow.document
  doc.open()
  doc.write(htmlDoc)
  doc.close()

  await new Promise((resolve) => {
    if (iframe.contentDocument?.readyState === 'complete') resolve()
    else iframe.onload = () => resolve()
  })

  const images = Array.from(doc.images || [])
  await Promise.all(
    images.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = () => resolve()
              img.onerror = () => resolve()
            })
    )
  )
  await new Promise((r) => setTimeout(r, 300))

  const pages = Array.from(doc.body.querySelectorAll('.proposal-page'))
  if (!pages.length) {
    document.body.removeChild(iframe)
    throw new Error('Template da proposta HangSix não pôde ser renderizado.')
  }

  const pdf = new jsPDF('p', 'mm', 'a4')
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  let primeiraPagina = true

  for (const pageEl of pages) {
    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: pageEl.scrollWidth,
      height: pageEl.scrollHeight
    })
    const imgData = canvas.toDataURL('image/jpeg', 0.92)
    const imgH = (canvas.height * pdfW) / canvas.width
    let offsetY = 0
    while (offsetY < imgH - 0.3) {
      if (!primeiraPagina) pdf.addPage()
      primeiraPagina = false
      pdf.addImage(imgData, 'JPEG', 0, -offsetY, pdfW, imgH)
      offsetY += pdfH
    }
  }

  document.body.removeChild(iframe)

  const base = dados.fileNameBase
    ? dados.fileNameBase.replace(/^Proposta(_Solar)?/, 'Proposta_HangSix')
    : `Proposta_HangSix_${slugNomeArquivo(dados.nomeCliente)}_${new Date().toISOString().split('T')[0]}`
  const fileName = `${base}.pdf`
  pdf.save(fileName)

  const htmlFileName = nomeArquivoHtmlDePdf(fileName)
  const htmlPortavel = await htmlComImagensEmbutidas(htmlDoc)
  await new Promise((r) => setTimeout(r, 400))
  salvarHtmlProposta(htmlPortavel, htmlFileName)

  return { fileName, htmlFileName, tipo: 'pdf' }
}
